import { newE2EPage } from "@stencil/core/testing";
import { testValidHtml } from "../../../test-utils/html-validate";

describe("input", () => {
    it("should render a component", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input></ux-input>`);
        const el = await page.find("ux-input");
        expect(el).toHaveClass("hydrated");
    });

    it("should be valid html", async () => {
        await testValidHtml("ux-input");
    });

    it("should focus on input when label is clicked and add class-name to host", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input label="foo"></ux-input>`);
        await page.waitForChanges();
        const label = await page.find("ux-input >>> label");
        await label.click();
        await page.waitForChanges();
        const activeElNodeName = await page.evaluate(
            () =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                document.querySelector("ux-input").shadowRoot.activeElement!.nodeName
        );
        const host = await page.find("ux-input");
        expect(host).toHaveAttribute("focused");
        expect(activeElNodeName).toEqual("INPUT");
    });

    it("should return the internal input element when getInputElement is called", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input></ux-input>`);
        await page.waitForChanges();
        const inputTag = await page.evaluate(
            async () =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (
                    await document.querySelector("ux-input").getInputElement()
                ).tagName
        );
        expect(inputTag).toEqual("INPUT");
    });

    it("should add a span to the label when the input is required", async () => {
        // when input has attr "required", there should be a span with class "required" inside the label (inside the shadowDOM)
        const page = await newE2EPage();
        await page.setContent(`<ux-input label="foo" required></ux-input>`);
        await page.waitForChanges();
        const label = await page.find("ux-input >>> label");
        const requiredSpan = await label.find("span.required");
        expect(requiredSpan).toBeTruthy();
    });

    it("should mirror the typical input attributes from the host to the internal input", async () => {
        const attributes = {
            autocomplete: "on",
            autofocus: "",
            disabled: "",
            inputmode: "text",
            max: 10,
            maxlength: 20,
            min: 1,
            minlength: 2,
            name: "foo",
            placeholder: "baz",
            readonly: "",
            required: "",
            spellcheck: "true",
            step: 5,
            type: "email",
            value: "test"
        };
        const page = await newE2EPage();
        await page.setContent(`<ux-input></ux-input>`);
        await page.waitForChanges();
        const host = await page.find("ux-input");
        const testString = (key: string, value: string) => `${key}:${value}`;
        for (const [key, value] of Object.entries(attributes)) {
            host.setAttribute(key, value);
            await page.waitForChanges();
            const attributeValue = await page.evaluate(async (key) => {
                const input = await document.querySelector("ux-input").getInputElement();
                if (key === "value") {
                    return input.value;
                }
                if (!input.hasAttribute(key)) {
                    return "missing attribute";
                }
                return input.getAttribute(key);
            }, key);
            expect(testString(key, attributeValue)).toEqual(testString(key, value.toString()));
        }
        expect(host).toMatchSnapshot();
    });

    it("should display a hint", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input><span slot="hint">HINT</span></ux-input>`);
        await page.waitForChanges();
        const el = await page.find("ux-input");
        expect(el).toMatchSnapshot();
    });

    it("should remove any element from the unit slot that is not a ux-unit element", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input><div slot="unit" class="fake-unit">foo</div></ux-input>`);
        await page.waitForChanges();
        const unit = await page.find("ux-input .fake-unit");
        expect(unit).toBeNull();
    });

    it("should remove any element from the icon slot that is not a ux-icon element", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input><div slot="icon" class="fake-icon">foo</div></ux-input>`);
        await page.waitForChanges();
        const icon = await page.find("ux-input .fake-icon");
        expect(icon).toBeNull();
    });

    it("should render a ux-unit element in the correct slot", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input><ux-unit slot="unit" unit="foo"></ux-unit></ux-input>`);
        await page.waitForChanges();
        const input = await page.find("ux-input");
        expect(input).toMatchSnapshot();
    });

    it("should not show the clearable button when a unit is present", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-input clearable value="foo"><ux-unit slot="unit" unit="foo"></ux-unit></ux-input>`);
        await page.waitForChanges();
        const clear = await page.find("ux-input >>> .clear");
        expect(clear).toBeNull();
    });
});
